/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.13.0
 */

import { Fragment, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import ProgressBar from '@components/common/ProgressBar/ProgressBar';
import { useSubscriberIdContext } from '@pages/PageSubscriberId/context';
import { subscriberIdRegionIdSelector } from '@pages/PageSubscriberId/selectors';
import { stringFormatParams } from '@utils/stringFormat';

import { infoTranslationSelector } from '../selectors';
import { useGetPiggyBankQuery, useGetTariffQuery } from '../store';
import { SubscriberIdInfoDiscountGroup } from './SubscriberIdInfoDiscountGroup';
import { mapRemainsToDiscounts } from './utils/mapRemainsToDiscounts';

const SubscriberIdInfoPiggyBank = () => {
    const translation = useSelector(infoTranslationSelector());

    const { subscriberId, userType } = useSubscriberIdContext();
    const regionId = useSelector(subscriberIdRegionIdSelector(subscriberId, userType));

    const isChangesCalculatingRemainsAndRatePlanFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.CHANGES_CALCULATING_REMAINS_AND_RATE_PLAN),
    );

    const { data: subscriberInfoTariff } = useGetTariffQuery(
        { subscriberId, regionId },
        {
            skip: !regionId || !isChangesCalculatingRemainsAndRatePlanFeatureEnabled,
        },
    );

    const piggyBankRemainsMemoized = useMemo(() => {
        return mapRemainsToDiscounts(subscriberInfoTariff?.piggyBankRemains, translation.units);
    }, [subscriberInfoTariff?.piggyBankRemains, translation.units]);

    const { data: subscriberInfoPiggyBank } = useGetPiggyBankQuery(subscriberId, {
        skip: isChangesCalculatingRemainsAndRatePlanFeatureEnabled,
    });

    if (
        isChangesCalculatingRemainsAndRatePlanFeatureEnabled
            ? !piggyBankRemainsMemoized?.length
            : !subscriberInfoPiggyBank?.length
    )
        return null;

    return (
        <Fragment>
            <div className="subscriber-id-info__separate" />
            <div className="subscriber-id-info__piggy-bank">
                <div className="subscriber-id-info__block">
                    {isChangesCalculatingRemainsAndRatePlanFeatureEnabled ? (
                        !!piggyBankRemainsMemoized.length && (
                            <SubscriberIdInfoDiscountGroup
                                discounts={piggyBankRemainsMemoized}
                                titleTranslation={translation.piggyBank.title}
                            />
                        )
                    ) : (
                        <>
                            <div className="subscriber-id__title subscriber-id__title_light">
                                {translation.piggyBank.title}
                            </div>
                            <div>{translation.piggyBank.rangeTitle}</div>
                            {subscriberInfoPiggyBank?.map((piggyBank) => (
                                <ProgressBar
                                    isColorChangedOnValue
                                    className="subscriber-id-info__range"
                                    labelLeft={''}
                                    labelRight={stringFormatParams(translation.piggyBank.range, {
                                        unit: piggyBank.unit,
                                        value: piggyBank.value,
                                        valueTotal: piggyBank.valueTotal,
                                    })}
                                    value={piggyBank.value}
                                    valueTotal={piggyBank.valueTotal}
                                />
                            ))}
                        </>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default SubscriberIdInfoPiggyBank;
